
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { MaintenanceItem, Status, Priority, ViewMode, UpdatePayload } from './types';
import { fetchMaintenanceItems, updateMaintenanceItem, createMaintenanceItem, addMessageToMaintenanceItem, fetchMessagesForItem } from './features/maintenance/maintenanceService';
import Header from './features/facility/Header';
import MaintenanceCard from './features/maintenance/MaintenanceCard';
import { getPriorityFromApi, REVERSE_PRIORITY_MAP } from './features/maintenance/priorityHelper';
import ViewToggle from './components/ViewToggle';
import MaintenanceListItem from './features/maintenance/MaintenanceListItem';
import RefreshButton from './components/RefreshButton';
import AddModal from './features/maintenance/AddModal';
import FilterControls from './features/maintenance/FilterControls';
import MaintenanceDetailsPage from './features/maintenance/MaintenanceDetailsPage';
import AnalyticsModal from './features/analytics/AnalyticsModal';
import { facilities } from './constants';
import SearchBar from './components/SearchBar';
import MaintenanceKanbanBoard from './features/maintenance/MaintenanceKanbanBoard';
import Login from './features/auth/Login';

const SESSION_DURATION = 30 * 60 * 1000; // 30 minutes in milliseconds

/**
 * The main application component.
 */
const App: React.FC = () => {
  // State for authentication with session persistence logic
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(() => {
    try {
      const storedUser = localStorage.getItem('maintenance_user_id');
      const storedTime = localStorage.getItem('maintenance_login_time');
      
      if (storedUser && storedTime) {
        const lastLoginTime = parseInt(storedTime, 10);
        const now = Date.now();
        // Check if session is within the 30-minute window
        if (now - lastLoginTime < SESSION_DURATION) {
          return true;
        }
      }
    } catch (e) {
      console.error("Failed to restore session", e);
    }
    return false;
  });

  const [currentUser, setCurrentUser] = useState<string>(() => {
    try {
      const storedUser = localStorage.getItem('maintenance_user_id');
      const storedTime = localStorage.getItem('maintenance_login_time');
      
      if (storedUser && storedTime) {
        const lastLoginTime = parseInt(storedTime, 10);
        const now = Date.now();
        // Check if session is within the 30-minute window
        if (now - lastLoginTime < SESSION_DURATION) {
          return storedUser;
        }
      }
    } catch (e) {
      console.error("Failed to restore user", e);
    }
    return '';
  });

  // Effect to handle session sliding/clearing on mount
  useEffect(() => {
    if (isAuthenticated) {
      // If authenticated on load, slide the session window by updating the timestamp
      localStorage.setItem('maintenance_login_time', Date.now().toString());
    } else {
      // If not authenticated (expired or fresh), ensure storage is clean
      localStorage.removeItem('maintenance_user_id');
      localStorage.removeItem('maintenance_login_time');
    }
  }, [isAuthenticated]);

  const [items, setItems] = useState<MaintenanceItem[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isRefreshing, setIsRefreshing] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [viewMode, setViewMode] = useState<ViewMode>('grid');
  const [isAddModalOpen, setIsAddModalOpen] = useState<boolean>(false);
  const [isAnalyticsModalOpen, setIsAnalyticsModalOpen] = useState<boolean>(false);
  const [selectedItemId, setSelectedItemId] = useState<number | null>(null);
  const [isDetailsLoading, setIsDetailsLoading] = useState<boolean>(false);
  const [statusFilter, setStatusFilter] = useState<Status[]>([Status.Open, Status.InProgress]);
  const [priorityFilter, setPriorityFilter] = useState<Priority[]>([]);
  const [categoryFilter, setCategoryFilter] = useState<string[]>([]);
  const [selectedFacilityId, setSelectedFacilityId] = useState<number>(() => {
  const params = new URLSearchParams(window.location.search);
  const centreParam = params.get('centre');
    // Parse the param, or fallback to 3 if it's missing or invalid
    return centreParam ? parseInt(centreParam, 10) : 3;
  });
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [announcement, setAnnouncement] = useState<string>('');
  const [linkCopied, setLinkCopied] = useState<boolean>(false);

  const loadData = useCallback(async (isRefresh = false) => {
    if (isRefresh) {
      setIsRefreshing(true);
      setAnnouncement('Refreshing maintenance tasks...');
    } else {
      setIsLoading(true);
      setAnnouncement('Loading maintenance tasks...');
    }
    setError(null);
    try {
      const data = await fetchMaintenanceItems(selectedFacilityId, statusFilter);
      setItems(data || []);
      setAnnouncement(`Loaded ${data?.length || 0} maintenance tasks.`);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'An unknown error occurred.';
      setError(errorMessage);
      setAnnouncement(`Error loading tasks: ${errorMessage}`);
    } finally {
      setIsRefreshing(false);
      setIsLoading(false);
    }
  }, [selectedFacilityId, statusFilter]); 

  useEffect(() => {
    loadData();
  }, [loadData]);
  
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const centreParam = params.get('centre');
    if (centreParam) {
      const numericId = parseInt(centreParam, 10);
      if (!isNaN(numericId) && numericId !== selectedFacilityId) {
        setSelectedFacilityId(numericId);
      }
    }
  }, [selectedFacilityId]);

  const handleRefresh = () => loadData(true);

  const handleCopyLink = () => {
    navigator.clipboard.writeText("https://careconcierge.alutechcare.com/maintenance/").then(() => {
        setLinkCopied(true);
        setTimeout(() => setLinkCopied(false), 2000);
    });
  };

  const handleViewDetails = async (itemId: number) => {
    setSelectedItemId(itemId);
    setIsDetailsLoading(true);
    try {
        const messages = await fetchMessagesForItem(itemId);
        setItems(currentItems =>
            currentItems.map(item =>
                item.id === itemId ? { ...item, messages } : item
            )
        );
    } catch (err) {
        console.error("Failed to fetch details:", err);
    } finally {
        setIsDetailsLoading(false);
    }
  };

  /**
   * Navigates back from the details page to the main dashboard view.
   */
  const handleGoBackToDashboard = () => {
    setSelectedItemId(null);
  };

  /**
   * Updates a maintenance item with new data, both locally (optimistically) and on the server.
   * Also logs changes to status, priority, and category as structured messages in the activity feed.
   * @param {number} id - The ID of the item to update.
   * @param {UpdatePayload} payload - The data to update.
   */
  const handleUpdateItem = async (id: number, payload: UpdatePayload) => {
    try {
      const originalItem = items.find(item => item.id === id);
      if (!originalItem) return;

      if (payload.status && payload.status !== originalItem.status as Status) {
        const auditMessage = JSON.stringify({
            audit: { type: 'status_change', from: originalItem.status, to: payload.status }
        });
        addMessageToMaintenanceItem(id, auditMessage, currentUser); // Fire-and-forget
      }

      // Check for priority change and log it as a structured message
      const originalPriority = getPriorityFromApi(originalItem.priority);
      if (payload.priority && payload.priority !== originalPriority) {
          const auditMessage = JSON.stringify({
              audit: { type: 'priority_change', from: originalPriority, to: payload.priority }
          });
          addMessageToMaintenanceItem(id, auditMessage, currentUser); // Fire-and-forget
      }

      // Check for category change and log it as a structured message
      if ('issues_category' in payload && payload.issues_category !== originalItem.issues_category) {
        const auditMessage = JSON.stringify({
            audit: { type: 'category_change', from: originalItem.issues_category, to: payload.issues_category }
        });
        addMessageToMaintenanceItem(id, auditMessage, currentUser); // Fire-and-forget
      }
  
      // Optimistically update the UI with the new data
      const updatedItem = {
          ...originalItem,
          ...payload,
          priority: payload.priority ? String(REVERSE_PRIORITY_MAP[payload.priority]) : originalItem.priority,
      };

      setItems(prevItems => prevItems.map(item => item.id === id ? updatedItem : item));
      await updateMaintenanceItem(id, payload);
      
      const messages = await fetchMessagesForItem(id);
      setItems(currentItems => currentItems.map(item => item.id === id ? { ...item, messages } : item));
    } catch (error) {
        console.error("Update failed:", error);
        loadData(true); 
    }
  };

  const handleMarkAsCompleted = async (id: number) => {
    await handleUpdateItem(id, { status: Status.Completed });
  };
  
  /**
   * Creates a new maintenance item.
   * Uses an optimistic UI update for a responsive user experience.
   * @param {string} title - The title of the new task.
   * @param {string} description - The description of the new task.
   * @param {Priority} priority - The priority of the new task.
   */
  const handleAddItem = async (title: string, description: string, priority: Priority) => {
    // Create a temporary item for optimistic UI update
    const tempId = -Date.now(); // Negative timestamp as a temporary unique ID
    const optimisticItem: MaintenanceItem = {
      id: tempId,
      isOptimistic: true, // Flag to indicate this is a temporary item
      title: title,
      description: description,
      status: Status.Open,
      priority: String(REVERSE_PRIORITY_MAP[priority]),
      created_at: new Date().toISOString(),
      centre_id: selectedFacilityId,
      assigned_to_full_name: 'Pending assignment...',
      created_by_full_name: 'You',
      messages: [],
      history: [],
      order_id: null,
      assigned_to: 0,
      created_by: parseInt(currentUser) || 0, // Using logged in user ID
      completed_by_full_name: null,
      completed_by: null,
      issues_category: null,
      images: [],
    };
    // Add the optimistic item to the top of the list
    setItems(prevItems => [optimisticItem, ...prevItems]);
    setAnnouncement(`Creating new task: ${title}`);

    try {
      // Perform the API call to create the item using currentUser
      await createMaintenanceItem(title, description, priority, selectedFacilityId, currentUser);
      await loadData(true);
    } catch (error) {
      console.error("Create failed:", error);
      throw error;
    }
  };

  const handleAddMessage = async (itemId: number, messageText: string, imageUrl?: string) => {
    try {
      await addMessageToMaintenanceItem(itemId, messageText, currentUser, imageUrl);
      const newMessages = await fetchMessagesForItem(itemId);
      setItems(prevItems => prevItems.map(i => i.id === itemId ? { ...i, messages: newMessages } : i));
    } catch (error) {
      console.error("Add message failed:", error);
    }
  };

  const handleFacilityChange = (facilityId: number) => {
    setItems([]);
    setSelectedFacilityId(facilityId);
    setSelectedItemId(null); // Return to dashboard view when changing facility
  };
  
  /**
   * Handles user login.
   * @param {string} employeeId - The ID of the employee logging in.
   */
  const handleLogin = (employeeId: string) => {
    setCurrentUser(employeeId);
    setIsAuthenticated(true);
    // Store session details
    localStorage.setItem('maintenance_user_id', employeeId);
    localStorage.setItem('maintenance_login_time', Date.now().toString());
  };

  /**
   * Handles user logout.
   */
  const handleLogout = () => {
    setIsAuthenticated(false);
    setCurrentUser('');
    setItems([]); // Clear local data
    setSelectedItemId(null);
    // Clear session details
    localStorage.removeItem('maintenance_user_id');
    localStorage.removeItem('maintenance_login_time');
  };
  
  const filteredAndSortedItems = useMemo(() => {
    const priorityOrder: { [key in Priority]: number } = {
      [Priority.Critical]: 1, [Priority.High]: 2, [Priority.Medium]: 3, [Priority.Low]: 4,
    };
    
    const filtered = items.filter(item => {
        const priorityMatch = priorityFilter.length === 0 || priorityFilter.includes(getPriorityFromApi(item.priority));
        const normalizedQuery = (searchQuery || '').trim().toLowerCase();
        const searchMatch = normalizedQuery === '' ||
          String(item.id).includes(normalizedQuery) ||
          (item.title || '').toLowerCase().includes(normalizedQuery) ||
          (item.description || '').toLowerCase().includes(normalizedQuery) ||
          (item.assigned_to_full_name || '').toLowerCase().includes(normalizedQuery);
        const categoryMatch = categoryFilter.length === 0 || (item.issues_category !== null && categoryFilter.includes(item.issues_category));
        return priorityMatch && searchMatch && categoryMatch;
    });

    return filtered.sort((a, b) => {
      if (a.isOptimistic && !b.isOptimistic) return -1;
      if (!a.isOptimistic && b.isOptimistic) return 1;
      const priorityA = priorityOrder[getPriorityFromApi(a.priority)];
      const priorityB = priorityOrder[getPriorityFromApi(b.priority)];
      if (priorityA !== priorityB) return priorityA - priorityB;
      return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
    });
  }, [items, priorityFilter, searchQuery, categoryFilter]);

  const selectedItem = useMemo(() => items.find(item => item.id === selectedItemId), [items, selectedItemId]);
  const selectedFacility = useMemo(() => facilities.find(f => f.id === selectedFacilityId)!, [selectedFacilityId]);

  // If not authenticated, show the login page
  if (!isAuthenticated) {
    return <Login onLogin={handleLogin} />;
  }

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Live region for screen reader announcements */}
      <div className="sr-only" role="status" aria-live="polite" aria-atomic="true">
        {announcement}
      </div>
      <Header 
        selectedFacility={selectedFacility}
        onFacilityChange={handleFacilityChange}
        currentUser={currentUser}
        onLogout={handleLogout}
      />
      <main className="p-4 sm:p-6 lg:p-8">
        {selectedItem ? (
          <MaintenanceDetailsPage
            key={selectedItem.id}
            item={selectedItem}
            isLoadingMessages={isDetailsLoading}
            onGoBack={handleGoBackToDashboard}
            onAddMessage={handleAddMessage}
            onUpdate={handleUpdateItem}
            onRefreshData={handleRefresh}
            currentUser={currentUser}
          />
        ) : (
          <>
            <div className="flex flex-col md:flex-row md:justify-between md:items-center mb-6 gap-4">
              <h1 className="text-2xl sm:text-3xl font-bold text-gray-800">Issues / Maintenance</h1>
              <div className="flex flex-wrap items-center justify-start md:justify-end gap-3">
                  <SearchBar searchQuery={searchQuery} onSearchChange={setSearchQuery} />
                  <FilterControls 
                      status={statusFilter} 
                      onStatusChange={setStatusFilter} 
                      priority={priorityFilter} 
                      onPriorityChange={setPriorityFilter}
                      category={categoryFilter}
                      onCategoryChange={setCategoryFilter} 
                  />
                  
                  {/* Icon Actions */}
                  <button
                    onClick={handleCopyLink}
                    className="p-2 rounded-md bg-white border border-gray-300 text-gray-600 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition-all duration-200"
                    title={linkCopied ? "Copied" : "Copy Link to Current View"}
                  >
                    {linkCopied ? (
                        <svg className="h-5 w-5 text-green-600" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                    ) : (
                        <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
                        </svg>
                    )}
                  </button>
                  <RefreshButton onClick={handleRefresh} isLoading={isRefreshing} />

                  {/* Primary Actions */}
                  <button
                    onClick={() => setIsAnalyticsModalOpen(true)}
                    className="inline-flex items-center justify-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-teal-600 hover:bg-teal-700"
                  >
                    <svg className="-ml-1 mr-2 h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                    </svg>
                    Analytics
                  </button>
                  <button
                    onClick={() => setIsAddModalOpen(true)}
                    className="inline-flex items-center justify-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700"
                  >
                    <svg className="-ml-1 mr-2 h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                    </svg>
                    New Task
                  </button>

                  <ViewToggle viewMode={viewMode} onViewChange={setViewMode} />
              </div>
            </div>
            {isLoading ? (
                <div className="flex justify-center items-center h-64">
                    <svg className="animate-spin h-10 w-10 text-blue-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path></svg>
                </div>
            ) : error ? (
                <div className="text-center p-8 bg-red-100 text-red-700 rounded-lg">{error}</div>
            ) : filteredAndSortedItems.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-96 text-center p-8 bg-white rounded-lg shadow-sm border border-gray-200">
                    <svg className="h-20 w-20 text-gray-300 mb-6" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <h3 className="text-xl font-semibold text-gray-900 mb-2">No maintenance records found</h3>
                    <p className="text-base text-gray-500 max-w-md mx-auto">
                        {items.length === 0 
                            ? "There are currently no reported issues or maintenance tasks for this facility. Everything is in good order!" 
                            : "No tasks match your current search or filter criteria. Try adjusting your filters."}
                    </p>
                    {items.length > 0 && (
                         <button
                            onClick={() => {
                                setStatusFilter([]);
                                setPriorityFilter([]);
                                setCategoryFilter([]);
                                setSearchQuery('');
                            }}
                            className="mt-6 inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                        >
                            Clear all filters
                        </button>
                    )}
                </div>
            ) : (
                viewMode === 'kanban' ? (
                    <MaintenanceKanbanBoard items={filteredAndSortedItems} onViewDetails={handleViewDetails} onMarkAsCompleted={handleMarkAsCompleted} onItemStatusChange={(id, status) => handleUpdateItem(id, { status })} />
                ) : (
                    <div className={viewMode === 'grid' ? "grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6" : "bg-white rounded-lg shadow overflow-hidden"}>
                        {filteredAndSortedItems.map((item, idx) => (
                            viewMode === 'grid' ? 
                            <MaintenanceCard key={item.id} item={item} onViewDetails={() => handleViewDetails(item.id)} onMarkAsCompleted={handleMarkAsCompleted} /> :
                            <MaintenanceListItem key={item.id} item={item} isLast={idx === filteredAndSortedItems.length - 1} onViewDetails={() => handleViewDetails(item.id)} onMarkAsCompleted={handleMarkAsCompleted} />
                        ))}
                    </div>
                )
            )}
          </>
        )}
      </main>
      {isAddModalOpen && <AddModal onClose={() => setIsAddModalOpen(false)} onSave={handleAddItem} />}
      {isAnalyticsModalOpen && <AnalyticsModal selectedFacilityId={selectedFacilityId} onClose={() => setIsAnalyticsModalOpen(false)} />}
    </div>
  );
};

export default App;
